import { Injectable } from '@angular/core';
import { WS_API_URL } from '../../../../../services/config';
import { IMessage, RxStomp, RxStompState } from '@stomp/rx-stomp';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { STOMP_DEFAULT_CONFIG, TOPICS_URLS, TopicsEnum } from './constants';
import { TopicStoreType } from './models';

@Injectable({
  providedIn: 'root',
})
export class ServerMessagesService extends RxStomp {
  private readonly topicUrls = new Map<TopicsEnum, string>();
  private readonly topicStore = new Map<TopicsEnum, TopicStoreType>();

  constructor() {
    super();
  }

  setUpConnection(): void {
    if (this.active) {
      this.unsubscribeAll();
      this.deactivate();
    }

    this.configure({
      ...STOMP_DEFAULT_CONFIG,
      brokerURL: WS_API_URL,
    });

    this.activate();
    this.subscribeAll();
  }

  disconnect(): void {
    if (this.active) {
      this.unsubscribeAll();
      this.topicUrls.clear();
      this.deactivate();
    }
  }

  subscribeToTopic<T extends object>(topic: TopicsEnum, params?: T): Observable<unknown> {
    let url: string = TOPICS_URLS[topic];

    if (this.topicStore.has(topic)) {
      this.unsubscribe(topic);
    }

    if (params) {
      const entries: Array<[string, string | number]> = Object.entries(params);

      entries.forEach(([key, value]: [string, string | number]) => {
        url = url.replace(`:${key}`, `${value}`);
      });
    }

    this.topicUrls.set(topic, url);
    this.addTopicToStore(topic, url);

    return this.connectionState$.pipe(
      filter((state: RxStompState) => state === RxStompState.OPEN),
      distinctUntilChanged(),
      switchMap(() => this.topicStore.get(topic).topic$.pipe(finalize(() => this.topicStore.delete(topic)))),
    );
  }

  unsubscribe(topic: TopicsEnum): void {
    this.topicStore.get(topic).unsubscribe$.next(true);
  }

  private addTopicToStore(topic: TopicsEnum, url: string): void {
    const unsubscribe$: Subject<boolean> = new Subject();

    this.topicStore.set(topic, {
      topic$: this.subscribe(url).pipe(takeUntil(unsubscribe$.pipe(filter(Boolean)))),
      unsubscribe$,
    });
  }

  private subscribeAll(): void {
    this.topicUrls.forEach((url: string, topic: TopicsEnum) => {
      this.addTopicToStore(topic, url);
    });
  }

  private unsubscribeAll(): void {
    this.topicStore.forEach((value: TopicStoreType, topic: TopicsEnum) => {
      this.unsubscribe(topic);
    });
  }

  private subscribe<T>(destination: string): Observable<T> {
    return this.watch({ destination }).pipe(
      filter((message: IMessage) => !!message?.body),
      map((message: IMessage) => JSON.parse(message.body)),
    );
  }
}
